import { Inbox } from 'lucide-react';
import { type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { cn } from '@/shared/utils/cn/cn.util';

export interface EmptyStateProps {
  /** Caller-translated. Falls back to the kit's neutral copy. */
  title?: string;
  description?: string;
  icon?: ReactNode;
  action?: ReactNode;
  className?: string;
}

/**
 * "There is nothing here."
 *
 * The kit supplies only neutral fallback copy — "Nothing here yet". Anything
 * that names the domain ("No roles yet — create the first one") is the caller's
 * translated string from the caller's own namespace (W9), because a kit that
 * knew about roles would be a kit only this product could reuse.
 */
export function EmptyState({ title, description, icon, action, className }: EmptyStateProps) {
  const { t } = useTranslation('uiKit');

  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center gap-md px-xl py-3xl text-center',
        className,
      )}
    >
      <span className="text-muted-foreground">
        {icon ?? <Inbox aria-hidden className="h-8 w-8" />}
      </span>
      <p className="text-h3 text-foreground">{title ?? t('state.emptyTitle')}</p>
      {description === undefined ? null : (
        <p className="text-caption text-muted-foreground">{description}</p>
      )}
      {action}
    </div>
  );
}
